import * as React from 'react';
import { BgColorsOutlined, TableOutlined } from '@ant-design/icons';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $findCellNode,
  $findTableNode,
  $getTableCellNodeRect,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $isTableCellNode,
  $isTableRowNode,
  $isTableSelection,
  $mergeCells,
  $unmergeCell,
  TableCellHeaderStates,
  type TableCellNode,
} from '@lexical/table';
import { ColorPicker, Divider, Popover } from 'antd';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { createPortal } from 'react-dom';
import { mergeRegister } from '@lexical/utils';
import styles from '../lexical-editor.module.css';

const TABLE_BACKGROUND_PRESETS = [
  {
    colors: [
      '#ffffff',
      '#f5f5f5',
      '#fff1b8',
      '#ffd6e7',
      '#d9f7be',
      '#bae7ff',
      '#d3adf7',
    ],
    defaultOpen: true,
    label: 'Cell backgrounds',
  },
];

type TableSelectionState = {
  anchorCellKey: string | null;
  canMerge: boolean;
  canUnmerge: boolean;
  selectedColumnCount: number;
  selectedColumnIndexes: number[];
  selectedCells: TableCellNode[];
  selectedColor: string;
  selectedRowCount: number;
  selectedRowIndexes: number[];
};

interface TableActionMenuPluginProps {
  disabled?: boolean;
}

function getSelectionState(): TableSelectionState {
  const selection = $getSelection();

  if (!$isRangeSelection(selection) && !$isTableSelection(selection)) {
    return {
      anchorCellKey: null,
      canMerge: false,
      canUnmerge: false,
      selectedColumnCount: 0,
      selectedColumnIndexes: [],
      selectedCells: [],
      selectedColor: '',
      selectedRowCount: 0,
      selectedRowIndexes: [],
    };
  }

  const anchorCell = $findCellNode(selection.anchor.getNode());

  if (!anchorCell) {
    return {
      anchorCellKey: null,
      canMerge: false,
      canUnmerge: false,
      selectedColumnCount: 0,
      selectedColumnIndexes: [],
      selectedCells: [],
      selectedColor: '',
      selectedRowCount: 0,
      selectedRowIndexes: [],
    };
  }

  const uniqueCells = new Map<string, TableCellNode>();
  const selectedColumnIndexes = new Set<number>();
  const selectedRowIndexes = new Set<number>();

  for (const node of selection.getNodes()) {
    const cellNode = $isTableCellNode(node) ? node : $findCellNode(node);

    if (cellNode) {
      uniqueCells.set(cellNode.getKey(), cellNode);

      const cellRect = $getTableCellNodeRect(cellNode);

      if (cellRect) {
        for (
          let rowIndex = cellRect.rowIndex;
          rowIndex < cellRect.rowIndex + cellRect.rowSpan;
          rowIndex += 1
        ) {
          selectedRowIndexes.add(rowIndex);
        }

        for (
          let columnIndex = cellRect.columnIndex;
          columnIndex < cellRect.columnIndex + cellRect.colSpan;
          columnIndex += 1
        ) {
          selectedColumnIndexes.add(columnIndex);
        }
      }
    }
  }

  if (!uniqueCells.has(anchorCell.getKey())) {
    uniqueCells.set(anchorCell.getKey(), anchorCell);
  }

  const selectedCells = Array.from(uniqueCells.values());

  return {
    anchorCellKey: anchorCell.getKey(),
    canMerge: selectedCells.length > 1,
    canUnmerge: anchorCell.getColSpan() > 1 || anchorCell.getRowSpan() > 1,
    selectedColumnCount: selectedColumnIndexes.size,
    selectedColumnIndexes: Array.from(selectedColumnIndexes).sort((a, b) => a - b),
    selectedCells,
    selectedColor: anchorCell.getBackgroundColor() ?? '',
    selectedRowCount: selectedRowIndexes.size,
    selectedRowIndexes: Array.from(selectedRowIndexes).sort((a, b) => a - b),
  };
}

function getCountLabel(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function TableActionItem({
  danger = false,
  label,
  onClick,
}: {
  danger?: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className={styles.tableActionMenuItem}
      data-danger={danger}
      onClick={onClick}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      type="button"
    >
      {label}
    </button>
  );
}

export function TableActionMenuPlugin({
  disabled = false,
}: TableActionMenuPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [isOpen, setIsOpen] = React.useState(false);
  const [position, setPosition] = React.useState<{ left: number; top: number } | null>(null);
  const [selectionState, setSelectionState] = React.useState<TableSelectionState>({
    anchorCellKey: null,
    canMerge: false,
    canUnmerge: false,
    selectedColumnCount: 0,
    selectedColumnIndexes: [],
    selectedCells: [],
    selectedColor: '',
    selectedRowCount: 0,
    selectedRowIndexes: [],
  });

  const refreshPosition = React.useCallback((anchorCellKey: string | null) => {
    if (!anchorCellKey) {
      setPosition(null);
      return;
    }

    const anchorElement = editor.getElementByKey(anchorCellKey);

    if (!(anchorElement instanceof globalThis.HTMLElement)) {
      setPosition(null);
      return;
    }

    const rect = anchorElement.getBoundingClientRect();

    setPosition({
      left: rect.right - 36,
      top: rect.top + 8,
    });
  }, [editor]);

  React.useEffect(() => {
    if (disabled) {
      setPosition(null);
      setSelectionState({
        anchorCellKey: null,
        canMerge: false,
        canUnmerge: false,
        selectedColumnCount: 0,
        selectedColumnIndexes: [],
        selectedCells: [],
        selectedColor: '',
        selectedRowCount: 0,
        selectedRowIndexes: [],
      });
      return;
    }

    const readTableSelection = () => {
      const nextState = editor.getEditorState().read(() => getSelectionState());

      setSelectionState(nextState);
      setIsOpen(false);
      refreshPosition(nextState.anchorCellKey);
    };

    readTableSelection();

    return mergeRegister(
      editor.registerUpdateListener(() => {
        readTableSelection();
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          readTableSelection();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [disabled, editor, refreshPosition]);

  React.useEffect(() => {
    if (!selectionState.anchorCellKey) {
      return;
    }

    const handleWindowChange = () => {
      refreshPosition(selectionState.anchorCellKey);
    };

    globalThis.window?.addEventListener('resize', handleWindowChange);
    globalThis.document?.addEventListener('scroll', handleWindowChange, true);

    return () => {
      globalThis.window?.removeEventListener('resize', handleWindowChange);
      globalThis.document?.removeEventListener(
        'scroll',
        handleWindowChange,
        true
      );
    };
  }, [refreshPosition, selectionState.anchorCellKey]);

  const applyCellBackground = React.useCallback(
    (nextColor: string) => {
      editor.update(() => {
        const currentSelectionState = getSelectionState();

        for (const cell of currentSelectionState.selectedCells) {
          cell.setBackgroundColor(nextColor || null);
        }
      });
    },
    [editor]
  );

  const mergeSelectedCells = React.useCallback(() => {
    editor.update(() => {
      const currentSelectionState = getSelectionState();

      if (currentSelectionState.selectedCells.length > 1) {
        $mergeCells(currentSelectionState.selectedCells);
      }
    });

    setIsOpen(false);
  }, [editor]);

  const unmergeSelectedCell = React.useCallback(() => {
    editor.update(() => {
      $unmergeCell();
    });

    setIsOpen(false);
  }, [editor]);

  const applyTableAction = React.useCallback(
    (
      action:
        | 'delete-column'
        | 'delete-row'
        | 'delete-table'
        | 'insert-column-left'
        | 'insert-column-right'
        | 'insert-row-above'
        | 'insert-row-below'
        | 'toggle-header-column'
        | 'toggle-header-row'
    ) => {
      editor.update(() => {
        const currentSelectionState = getSelectionState();
        const selection = $getSelection();

        if (
          (!$isRangeSelection(selection) && !$isTableSelection(selection)) ||
          !currentSelectionState.anchorCellKey
        ) {
          return;
        }

        const tableNode = $findTableNode(selection.anchor.getNode());

        if (!tableNode) {
          return;
        }

        if (action === 'insert-row-above') {
          for (
            let index = 0;
            index < Math.max(currentSelectionState.selectedRowCount, 1);
            index += 1
          ) {
            $insertTableRowAtSelection(false);
          }
          return;
        }

        if (action === 'insert-row-below') {
          for (
            let index = 0;
            index < Math.max(currentSelectionState.selectedRowCount, 1);
            index += 1
          ) {
            $insertTableRowAtSelection(true);
          }
          return;
        }

        if (action === 'insert-column-left') {
          for (
            let index = 0;
            index < Math.max(currentSelectionState.selectedColumnCount, 1);
            index += 1
          ) {
            $insertTableColumnAtSelection(false);
          }
          return;
        }

        if (action === 'insert-column-right') {
          for (
            let index = 0;
            index < Math.max(currentSelectionState.selectedColumnCount, 1);
            index += 1
          ) {
            $insertTableColumnAtSelection(true);
          }
          return;
        }

        if (action === 'delete-row') {
          for (
            let index = 0;
            index < Math.max(currentSelectionState.selectedRowCount, 1);
            index += 1
          ) {
            $deleteTableRowAtSelection();
          }
          return;
        }

        if (action === 'delete-column') {
          for (
            let index = 0;
            index < Math.max(currentSelectionState.selectedColumnCount, 1);
            index += 1
          ) {
            $deleteTableColumnAtSelection();
          }
          return;
        }

        if (action === 'toggle-header-row' || action === 'toggle-header-column') {
          const tableCells = tableNode
            .getChildren()
            .filter($isTableRowNode)
            .flatMap((rowNode) => rowNode.getChildren().filter($isTableCellNode));
          const isRowAction = action === 'toggle-header-row';
          const targetIndexes = new Set(
            isRowAction
              ? currentSelectionState.selectedRowIndexes
              : currentSelectionState.selectedColumnIndexes
          );
          const targetCells = tableCells.filter((cellNode) => {
            const cellRect = $getTableCellNodeRect(cellNode);

            if (!cellRect) {
              return false;
            }

            if (isRowAction) {
              for (
                let rowIndex = cellRect.rowIndex;
                rowIndex < cellRect.rowIndex + cellRect.rowSpan;
                rowIndex += 1
              ) {
                if (targetIndexes.has(rowIndex)) {
                  return true;
                }
              }

              return false;
            }

            for (
              let columnIndex = cellRect.columnIndex;
              columnIndex < cellRect.columnIndex + cellRect.colSpan;
              columnIndex += 1
            ) {
              if (targetIndexes.has(columnIndex)) {
                return true;
              }
            }

            return false;
          });
          const headerState = isRowAction
            ? TableCellHeaderStates.ROW
            : TableCellHeaderStates.COLUMN;
          const shouldEnableHeader = targetCells.some(
            (cellNode) => !cellNode.hasHeaderState(headerState)
          );

          for (const cellNode of targetCells) {
            cellNode.setHeaderStyles(
              shouldEnableHeader
                ? headerState
                : TableCellHeaderStates.NO_STATUS,
              headerState
            );
          }

          return;
        }

        const paragraphNode = $createParagraphNode();
        tableNode.insertAfter(paragraphNode);
        paragraphNode.selectStart();
        tableNode.remove();
      });

      setIsOpen(false);
    },
    [editor]
  );

  if (disabled || !position || !selectionState.anchorCellKey) {
    return null;
  }

  if (typeof globalThis.document === 'undefined') {
    return null;
  }

  const rowCountLabel = getCountLabel(
    Math.max(selectionState.selectedRowCount, 1),
    'row',
    'rows'
  );
  const columnCountLabel = getCountLabel(
    Math.max(selectionState.selectedColumnCount, 1),
    'column',
    'columns'
  );

  return createPortal(
    <div
      className={styles.tableActionButtonWrap}
      style={{
        left: `${position.left}px`,
        top: `${position.top}px`,
      }}
    >
      <Popover
        content={
          <div className={styles.tableActionMenu}>
            {selectionState.canMerge && (
              <TableActionItem label="Merge cells" onClick={mergeSelectedCells} />
            )}
            {selectionState.canUnmerge && (
              <TableActionItem
                label="Unmerge cell"
                onClick={unmergeSelectedCell}
              />
            )}
            {(selectionState.canMerge || selectionState.canUnmerge) && (
              <Divider className={styles.tableActionDivider} />
            )}
            <TableActionItem
              label={`Insert ${rowCountLabel} above`}
              onClick={() => {
                applyTableAction('insert-row-above');
              }}
            />
            <TableActionItem
              label={`Insert ${rowCountLabel} below`}
              onClick={() => {
                applyTableAction('insert-row-below');
              }}
            />
            <TableActionItem
              label={`Insert ${columnCountLabel} left`}
              onClick={() => {
                applyTableAction('insert-column-left');
              }}
            />
            <TableActionItem
              label={`Insert ${columnCountLabel} right`}
              onClick={() => {
                applyTableAction('insert-column-right');
              }}
            />
            <TableActionItem
              label={`Toggle header on ${rowCountLabel}`}
              onClick={() => {
                applyTableAction('toggle-header-row');
              }}
            />
            <TableActionItem
              label={`Toggle header on ${columnCountLabel}`}
              onClick={() => {
                applyTableAction('toggle-header-column');
              }}
            />
            <TableActionItem
              danger
              label={`Delete ${rowCountLabel}`}
              onClick={() => {
                applyTableAction('delete-row');
              }}
            />
            <TableActionItem
              danger
              label={`Delete ${columnCountLabel}`}
              onClick={() => {
                applyTableAction('delete-column');
              }}
            />
            <TableActionItem
              danger
              label="Delete table"
              onClick={() => {
                applyTableAction('delete-table');
              }}
            />
            <Divider className={styles.tableActionDivider} />
            <ColorPicker
              allowClear
              onChange={(_, css) => {
                applyCellBackground(css);
              }}
              onClear={() => {
                applyCellBackground('');
              }}
              placement="bottomRight"
              presets={TABLE_BACKGROUND_PRESETS}
              showText={false}
              trigger="click"
              value={selectionState.selectedColor || '#ffffff'}
            >
              <button
                className={styles.tableActionColorRow}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                type="button"
              >
                <span className={styles.tableActionLabel}>Background</span>
                <span className={styles.tableActionColorButton}>
                  <BgColorsOutlined />
                  <span
                    className={styles.tableActionColorSwatch}
                    style={{
                      backgroundColor:
                        selectionState.selectedColor || '#ffffff',
                    }}
                  />
                </span>
              </button>
            </ColorPicker>
          </div>
        }
        onOpenChange={setIsOpen}
        open={isOpen}
        placement="bottomRight"
        trigger="click"
      >
        <button
          aria-label="Table actions"
          className={styles.tableActionButton}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          type="button"
        >
          <TableOutlined />
        </button>
      </Popover>
    </div>,
    globalThis.document.body
  );
}
